import { Fragment, useEffect } from "react";
import {
  createPath,
  Link as RouterLink,
  Navigate,
  Outlet,
  parsePath,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { Box, Link, Typography } from "@mui/material";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { CicdBreadcrumbs } from "~/pages/cicd/CicdBreadcrumbs";
import { Space } from "~/lib/types";
import { ScrollToTop } from "~/lib/scroll-to-top";
import {
  CaseStatus,
  ExceptionGroup,
  ExceptionType,
  UiAction,
  UiPage,
  useGetAssetLinkInfoQuery,
  useGetCasesCountQuery,
  useGetClientIntegrationQuery,
  useLoadAssetQuery,
  useLoadOrganizationForSpaceQuery,
  useLoadServiceAccountQuery,
  useRegisterUserActionMutation,
} from "~/operations";
import {
  TabListItem,
  TabNavigation,
  useRouteMatch,
} from "~/components/tab-nav";
import { useFetchExceptions } from "~/components/exceptions/use-fetch-exceptions";
import { PendingExceptionsTooltip } from "~/components/exceptions/pending-exceptions-tooltip";
import { AssetOverviewHeader } from "./components/PolicyScoreCard/asset/assetOverviewHeader/assetOverviewHeader";
import { Asset } from "./asset/types";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { getAssetMrn } from "~/lib/urlUtils";
import { setDocumentTitle } from "~/lib/commonUtils";
import { MONDOO_PLATFORM_VULN_POLICY_PREFIX } from "../../../constants";
import { useCanListCases } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/ListCases";
import { useCanGetServiceAccount } from "~/lib/iam/actions/mondoo/agents/AgentManager/GetServiceAccount";
import { useSpaceSettings } from "~/pages/space-settings/hooks/useSpaceSettings";
import { Project } from "~/pages/cicd/utils/cicdTypes";
import { integrationDetailHref } from "../integrations/utils/integrationLinksUtils";

type Props = {
  space: Space;
  scope: SpaceOrWorkspaceScope;
  isCicd?: boolean;
  project?: Project | null;
  assetMrn?: string;
};

export type AssetIntegration = {
  mrn: string;
  name: string;
  href: string;
  status: string;
};

export type AssetServiceAccount = {
  mrn: string;
  name: string;
  href: string;
  status: string;
};

export type AssetOutletContextType = {
  loading: boolean;
  asset: Asset;
  assetId: string;
  assetMrn: string;
  assetPath: string;
  assetIntegration?: AssetIntegration;
  assetServiceAccount?: AssetServiceAccount;
  isCicd?: boolean;
  space: Space;
  scope: SpaceOrWorkspaceScope;
  hasMondooPlatformVulnPolicy: boolean;
  exceptionGroups: ExceptionGroup[];
  isVulnerabilityScanningEnabled: boolean;
};

export function AssetPage({
  space,
  project,
  isCicd = false,
  scope,
  ...props
}: Props) {
  const { id } = useParams();

  const assetId = props.assetMrn?.split("/").pop() || id;

  if (!assetId) {
    return <LoadingFailedPage what="asset" />;
  }

  const assetMrn = props.assetMrn || getAssetMrn(space.id, assetId);

  const { exceptionGroups, pendingExceptionsGroups } = useFetchExceptions({
    scopeMrn: assetMrn,
    types: [
      ExceptionType.Security,
      ExceptionType.Advisory,
      ExceptionType.Cve,
      ExceptionType.Fex,
    ],
    includeDeleted: true,
    includeExtension: true,
    skip: !assetMrn,
  });

  const [registerUserAction] = useRegisterUserActionMutation();

  const { pvcIsEnabled } = useSpaceSettings({ space });

  const { data: orgDataForSpace, loading: orgDataLoading } =
    useLoadOrganizationForSpaceQuery({
      variables: { spaceMrn: space.mrn },
    });

  const hasListTicketsPermissions = useCanListCases(space);

  // Send user action mutation on mount and when the tab changes
  useEffect(() => {
    if (orgDataLoading) return;
    let currentTab = window.location.pathname.split("/").pop() || "unknown";
    const org = orgDataForSpace?.space?.organization;
    let orgMrn = org?.mrn ? org.mrn : "unknown";

    const page = isCicd ? UiPage.CicdJob : UiPage.Asset;

    registerUserAction({
      variables: {
        input: {
          action: UiAction.View,
          page: page,
          scopeMrn: space.mrn,
          details: currentTab,
        },
      },
    });

    return () => {};
  }, [window.location.pathname, orgDataForSpace]);

  const {
    data: assetData,
    error,
    loading,
  } = useLoadAssetQuery({
    variables: {
      mrn: assetMrn,
    },
  });

  const { data: assetLinkData } = useGetAssetLinkInfoQuery({
    variables: {
      input: {
        spaceID: space.id,
        assetId,
      },
    },
  });

  const { data: tickets } = useGetCasesCountQuery({
    variables: {
      input: {
        scopeMrn: assetMrn,
        findingMrns: [],
        statuses: [CaseStatus.Open],
        contextMrn: scope.mrn,
      },
    },
    fetchPolicy: "cache-and-network",
    skip: !hasListTicketsPermissions,
  });

  const integrationMrn = assetData?.asset?.labels.find(
    ({ key }) => key === "mondoo.com/integration-mrn",
  )?.value;

  const { data: integrationData } = useGetClientIntegrationQuery({
    variables: { mrn: integrationMrn || "skip" },
    skip: !integrationMrn,
  });

  const { clientIntegration } = integrationData || {};
  const { integration } = clientIntegration || {};
  const assetIntegration: AssetIntegration | undefined =
    integrationMrn && integration
      ? {
          name: integration.name,
          mrn: integration.mrn,
          href: integrationDetailHref(integration.type, integration.mrn, space),
          status: integration.status,
        }
      : integrationMrn && !integration
        ? {
            name: "Integration removed",
            mrn: integrationMrn,
            href: "",
            status: "removed",
          }
        : undefined;

  const serviceAccountMrn = assetData?.asset?.labels.find(
    ({ key }) => key === "mondoo.com/agent-mrn",
  )?.value;

  const hasServiceAccountPermission = useCanGetServiceAccount(space);
  const { data: serviceAccountData } = useLoadServiceAccountQuery({
    variables: { mrn: serviceAccountMrn || "skip" },
    skip: !serviceAccountMrn || !hasServiceAccountPermission,
  });

  const { serviceAccount } = serviceAccountData || {};
  const serviceAccountId = serviceAccountMrn?.split("/").pop();
  const assetServiceAccount: AssetServiceAccount | undefined = serviceAccount
    ? {
        name: serviceAccount.name,
        mrn: serviceAccount.mrn,
        href: `/space/settings/serviceaccounts?${scope.params}&queryTerms=${serviceAccountId}`,
        status: "ok",
      }
    : serviceAccountMrn && hasServiceAccountPermission && !serviceAccount
      ? {
          name: "Service account removed",
          mrn: serviceAccountMrn,
          href: "",
          status: "removed",
        }
      : undefined;

  const assetLinkInfo = assetLinkData?.getAssetLinkInfo;
  const asset = assetData?.asset;

  if (loading) {
    return <LoadingPage what="asset" />;
  }

  if (error || !asset) {
    return <LoadingFailedPage what="asset" />;
  }

  let assetPath = `/space/inventory/${assetId}?${scope.params}`;

  if (assetLinkInfo?.__typename === "CiAssetLinkInfo") {
    const projectId = assetLinkInfo.projectID.split("/").pop();
    assetPath = `/space/cicd/jobs/${projectId}?${scope.params}&projectId=${projectId}&jobId=${assetMrn}`;

    if (!isCicd) {
      return <Navigate to={assetPath} replace />;
    }
  }

  const generateHref = (tab: string): string => {
    const parsedPath = parsePath(assetPath);
    const pathname = `${parsedPath.pathname}/${tab}`;
    const search = parsedPath.search;
    return createPath({ pathname, search });
  };

  const tabList: TabListItem[] = [
    { label: "Overview", to: generateHref("overview"), route: "/overview" },
    {
      label: "Policies",
      to: generateHref("policies"),
      route: "/policies",
    },
    {
      label: "Findings",
      to: generateHref("findings"),
      route: "/findings",
    },
    {
      label: "Data queries",
      to: generateHref("data-queries"),
      route: "/data-queries",
    },
    // display the software, vulnerabilities, and advisories tabs if the asset
    // has Platform Vulnerability Policy applied and the asset is an operating system
    ...(pvcIsEnabled
      ? [
          {
            label: "Software",
            to: generateHref("software"),
            route: "/software",
          },
        ]
      : []),
    !isCicd
      ? {
          label: (
            <Box sx={{ display: "flex", alignItems: "center" }} gap={1}>
              Exceptions
              {pendingExceptionsGroups.length > 0 && (
                <PendingExceptionsTooltip />
              )}
            </Box>
          ),
          to: generateHref("exceptions"),
          route: "/exceptions",
        }
      : undefined,
  ].flatMap((tab) => (tab ? [tab] : []));

  const currentTab = useRouteMatch(
    tabList.map((x) => x.route),
    "overview",
  );
  const trail = isCicd
    ? [
        <Link key="1" component={RouterLink} to={`/space/cicd?${scope.params}`}>
          CI/CD
        </Link>,
        <Link
          key="2"
          component={RouterLink}
          to={`/space/cicd/jobs?${scope.params}&projectId=${project?.id}`}
        >
          {project?.name}
        </Link>,
        <Typography key="3">{asset.name}</Typography>,
      ]
    : [
        <Link
          key="1"
          component={RouterLink}
          to={`/space/inventory?${scope.params}`}
        >
          Inventory
        </Link>,
        <Typography key="2">{asset.name}</Typography>,
      ];

  // TODO: check settings for platform-vulnerability enabled instead
  const hasMondooPlatformVulnPolicy = Boolean(
    asset?.listPolicies?.edges.some(
      (policy) => policy.node.mrn === MONDOO_PLATFORM_VULN_POLICY_PREFIX,
    ),
  );

  setDocumentTitle([asset.name, "Assets"]);

  return (
    <Fragment>
      <ScrollToTop />
      <CicdBreadcrumbs {...{ scope, trail }} />

      <AssetOverviewHeader
        asset={asset}
        space={space}
        scope={scope}
        project={project}
        isCicd={isCicd}
        ticketsCount={tickets?.cases?.totalCount || 0}
      />

      {asset && (
        <Fragment>
          {/* Navigation Tabs List */}
          <TabNavigation
            {...{ id: "asset-tabs", tabList, currentTab, marginBottom: 0 }}
          />

          <Outlet
            context={
              {
                loading,
                asset,
                assetMrn,
                assetPath,
                assetId,
                exceptionGroups,
                space,
                scope,
                hasMondooPlatformVulnPolicy,
                isCicd,
                isVulnerabilityScanningEnabled: pvcIsEnabled,
                assetIntegration,
                assetServiceAccount,
              } satisfies AssetOutletContextType
            }
          />
        </Fragment>
      )}
    </Fragment>
  );
}

export function useAssetOutlet() {
  return useOutletContext<AssetOutletContextType>();
}
